import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirm, useToast } from '@/ui';
import {
  acceptChannelIntake,
  getCurrentChannelTerms,
  rejectChannelIntake,
} from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import { apiErrorName } from '@/lib/apiErrorName';
import {
  AFTER_COMMIT_ERRORS,
  acceptFailure,
  termsLookupFailure,
} from './acceptCopy';

// Accept/reject flow for WhatsApp intakes: busy ids, the preflight lock, the
// terms lookup, the accept dialog (case coordinates) and the reject confirm,
// the mutations and the queue invalidation.
// `onAccept` and `onReject` read only `{ id, sender }`, so a caller can pass a
// queue row or an intake detail.
export function useIntakeDecisions({ organizationId }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [busyIds, setBusyIds] = useState(() => new Set());
  const busyIdsRef = useRef(busyIds);
  // Blocks every Aceptar/Rechazar while terms lookup + confirm run. Ref is the
  // sync guard (two clicks before paint); state drives disabled styling.
  const [preflightOpen, setPreflightOpen] = useState(false);
  const preflightRef = useRef(false);

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: qk.channelIntakeQueue.all() }),
    [queryClient],
  );

  const markBusy = useCallback((id) => {
    const nextBusyIds = new Set(busyIdsRef.current);
    nextBusyIds.add(id);
    busyIdsRef.current = nextBusyIds;
    setBusyIds(nextBusyIds);
  }, []);

  const clearBusy = useCallback((id) => {
    const nextBusyIds = new Set(busyIdsRef.current);
    nextBusyIds.delete(id);
    busyIdsRef.current = nextBusyIds;
    setBusyIds(nextBusyIds);
  }, []);

  const beginPreflight = useCallback(() => {
    if (preflightRef.current) return false;
    preflightRef.current = true;
    setPreflightOpen(true);
    return true;
  }, []);

  const endPreflight = useCallback(() => {
    preflightRef.current = false;
    setPreflightOpen(false);
  }, []);

  // The accept dialog holds the preflight lock until it closes, so no other
  // row can be decided meanwhile. `submittingRef` is the sync guard against a
  // double submit before the mutation reports pending.
  const [acceptDialog, setAcceptDialog] = useState(null);
  const submittingRef = useRef(false);

  const acceptMut = useMutation({
    mutationFn: ({ id, body }) =>
      acceptChannelIntake(id, { organizationId, body }),
    onMutate: ({ id }) => markBusy(id),
    onSuccess: (data) => {
      toast({
        kind: 'success',
        message: data?.linkedCase?.alreadyFollowing
          ? 'Pedido aceptado. La causa ya estaba seguida por la cuenta personal.'
          : 'Pedido aceptado y causa creada.',
      });
      setAcceptDialog(null);
      endPreflight();
    },
    onError: (e, { body }) =>
      setAcceptDialog(
        (current) =>
          current && {
            ...current,
            error: acceptFailure(e),
            retryBody: e?.status >= 500 ? body : current.retryBody,
            // Only these two answers come after the accept committed. A 502
            // from a gateway says nothing about the intake.
            accepted:
              AFTER_COMMIT_ERRORS.has(apiErrorName(e?.body)) ||
              current.accepted,
          },
      ),
    // The refetch is what takes the decided row off the queue, so the busy flag
    // has to outlive it. Clearing first re-enables the controls over a row that
    // still reads `pending`, and the second click reaches an intake the server
    // already resolved.
    onSettled: (_data, _err, { id }) =>
      invalidate().finally(() => {
        clearBusy(id);
        submittingRef.current = false;
      }),
  });

  const rejectMut = useMutation({
    mutationFn: (id) => rejectChannelIntake(id, { organizationId }),
    onMutate: (id) => markBusy(id),
    onSuccess: () => {
      toast({ kind: 'success', message: 'Pedido rechazado.' });
    },
    onError: (e) =>
      toast({
        kind: 'err',
        title: 'No se pudo rechazar',
        message: e?.message ?? 'Error desconocido.',
      }),
    onSettled: (_data, _err, id) => invalidate().finally(() => clearBusy(id)),
  });

  const onAccept = useCallback(
    async ({ id, sender }) => {
      if (busyIdsRef.current.has(id) || !beginPreflight()) return;
      markBusy(id);
      let handedOff = false;
      try {
        if (!sender) {
          toast({
            kind: 'err',
            ...acceptFailure({
              body: { error: { name: 'ChannelIntakeIdentifierInvalid' } },
            }),
          });
          return;
        }
        const terms = await getCurrentChannelTerms({
          identifier: sender,
          organizationId,
        });
        setAcceptDialog({
          row: { id, sender },
          terms,
          error: null,
          retryBody: null,
        });
        handedOff = true;
      } catch (e) {
        toast({ kind: 'err', ...termsLookupFailure(e) });
      } finally {
        if (!handedOff) {
          endPreflight();
          clearBusy(id);
        }
      }
    },
    [beginPreflight, clearBusy, endPreflight, markBusy, organizationId, toast],
  );

  const closeAccept = useCallback(() => {
    if (submittingRef.current || !acceptDialog) return;
    if (acceptDialog.retryBody || acceptDialog.accepted) {
      toast({
        kind: 'warn',
        message: acceptDialog.accepted
          ? 'El pedido quedó aceptado sin causa. Créala desde Nueva causa.'
          : 'El pedido puede haber quedado aceptado sin causa. Si no aparece la causa, créala desde Nueva causa.',
      });
    }
    clearBusy(acceptDialog.row.id);
    setAcceptDialog(null);
    endPreflight();
  }, [acceptDialog, clearBusy, endPreflight, toast]);

  const submitAccept = useCallback(
    (body) => {
      if (
        submittingRef.current ||
        !acceptDialog ||
        acceptDialog.terms?.alreadyAccepted === false
      ) {
        return;
      }
      submittingRef.current = true;
      setAcceptDialog((current) => ({ ...current, error: null }));
      acceptMut.mutate({ id: acceptDialog.row.id, body });
    },
    [acceptDialog, acceptMut],
  );

  const onReject = useCallback(
    async ({ id, sender }) => {
      if (busyIdsRef.current.has(id) || !beginPreflight()) return;
      markBusy(id);
      let handedOff = false;
      try {
        const ok = await confirm({
          title: 'Rechazar pedido',
          body: (
            <>
              Vas a rechazar el hilo de <b>{sender || 'este pedido'}</b>. No
              crea causa.
            </>
          ),
          confirmLabel: 'Rechazar',
          confirmVariant: 'danger',
        });
        if (!ok) return;
        rejectMut.mutate(id);
        handedOff = true;
      } finally {
        endPreflight();
        if (!handedOff) clearBusy(id);
      }
    },
    [beginPreflight, clearBusy, confirm, endPreflight, markBusy, rejectMut],
  );

  // While an accept is in flight (including the queue refresh that follows) no
  // other row can be decided either.
  return {
    busyIds,
    preflightOpen: preflightOpen || acceptMut.isPending,
    accepting: acceptMut.isPending,
    acceptDialog,
    onAccept,
    onReject,
    closeAccept,
    submitAccept,
  };
}
